import type { ReactNode } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { AlertTriangle, CheckCircle2, Clock, LogOut } from "lucide-react";
import { Skeleton } from "@/components/ui/skeleton";
import { cn } from "@/lib/utils";
import type { PartStockState } from "@/lib/partStatus";

/** FIX-298: the part's stock state, shown first on the part page. */
interface Props {
  loading: boolean;
  state: PartStockState;
  bin?: { id: string; address: string; description?: string | null } | null;
  takenOut?: { reason: string; when: string | null; by: string | null; note: string | null } | null;
  /** Main action plus room for a second one (FIX-294: put in bin / move). */
  actions?: ReactNode;
}

const MIN_H = "min-h-[112px]";

/**
 * FIX-298: buttons inside the taken-out block use the block's own colours
 * (transparent, foreground border/text, light foreground hover) so they stay
 * readable on its dark background in light and dark mode.
 */
export const TAKEN_OUT_BUTTON_CLASS =
  "bg-transparent border-stock-out-foreground/60 text-stock-out-foreground hover:bg-stock-out-foreground/10 hover:text-stock-out-foreground";

export function PartStatusBlock({ loading, state, bin, takenOut, actions }: Props) {
  const { t } = useTranslation("inventory");

  if (loading) {
    return (
      <div
        className={cn(MIN_H, "rounded-xl border border-border bg-card p-5 flex items-center gap-4")}
        aria-busy="true"
        aria-label={t("part.loading")}
      >
        <Skeleton className="w-10 h-10 rounded-full shrink-0" />
        <div className="flex-1 space-y-2">
          <Skeleton className="h-4 w-24" />
          <Skeleton className="h-6 w-40" />
        </div>
      </div>
    );
  }

  const tone = {
    inStock: { box: "bg-stock-in-soft border-stock-in/40", icon: "text-stock-in", Icon: CheckCircle2, sub: "text-muted-foreground" },
    notInBin: { box: "bg-stock-pending-soft border-stock-pending/40", icon: "text-stock-pending", Icon: Clock, sub: "text-muted-foreground" },
    takenOut: { box: "bg-stock-out border-stock-out text-stock-out-foreground", icon: "text-stock-out-foreground", Icon: LogOut, sub: "text-stock-out-foreground/85" },
    damaged: { box: "bg-destructive/10 border-destructive/40", icon: "text-destructive", Icon: AlertTriangle, sub: "text-muted-foreground" },
  }[state];
  const { Icon } = tone;

  const label =
    state === "takenOut" && takenOut
      ? t("takeOut.badge", { reason: t(`takeOut.reasons.${takenOut.reason}`) })
      : t(`status.${state}`);

  return (
    <section
      className={cn(MIN_H, "rounded-xl border p-5 flex flex-col sm:flex-row sm:items-center gap-4", tone.box)}
      aria-label={label}
    >
      <div className="flex items-start gap-3 flex-1 min-w-0">
        <Icon className={cn("w-7 h-7 shrink-0 mt-0.5", tone.icon)} aria-hidden="true" />
        <div className="min-w-0">
          <div className={cn("text-base font-semibold", state === "takenOut" ? "" : "text-foreground")}>{label}</div>
          {state === "inStock" && bin && (
            <>
              <Link
                to={`/bin/${bin.id}`}
                className="block font-mono font-semibold text-primary hover:underline break-all"
                style={{ fontSize: 22, lineHeight: 1.25 }}
              >
                {bin.address}
              </Link>
              {bin.description && <div className={cn("text-sm", tone.sub)}>{bin.description}</div>}
            </>
          )}
          {state === "takenOut" && takenOut && (
            <>
              {(takenOut.when || takenOut.by) && (
                <div className={cn("text-sm", tone.sub)}>
                  {[takenOut.when, takenOut.by].filter(Boolean).join(" · ")}
                </div>
              )}
              {takenOut.note && <div className="text-sm mt-1">{takenOut.note}</div>}
            </>
          )}
        </div>
      </div>
      {actions && (
        <div className="flex flex-col sm:flex-row gap-2 w-full sm:w-auto shrink-0">{actions}</div>
      )}
    </section>
  );
}
